import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MessageCircle, Wifi, Coffee, BedDouble, Briefcase, KeyRound, Sparkles,
  BadgeCheck, CalendarRange, Clock,
} from "lucide-react";
import SmartImage from "../components/SmartImage";
import GuestReviews from "../components/GuestReviews";
import { fetchProperties } from "../lib/api";
import { useCMS } from "../lib/cms";

/* ------------------------------------------------------------------
   HOMEPAGE CONTENT — single source of truth (will become CMS in Phase 2)
   Every visible string lives here. Phase 2 will replace this object
   with a fetch from /api/site_content/home.
   ------------------------------------------------------------------ */
const HOMEPAGE_CONTENT = {
  hero: {
    overline: "A home for real life",
    line_a: "Live in London,",
    line_b: "not just visit",
    line_b_accent: "London.",
    sub: "A calm, comfortable studio in Hammersmith, West London. Perfect for short stays, longer stays, and everything in between.",
    cta_primary: { label: "Book Your Stay", href_resolver: (id) => (id ? `/properties/${id}` : "/properties") },
    cta_secondary: { label: "View The Studio", href: "#the-studio" },
  },
  promises: [
    { id: "price",    icon: BadgeCheck,    title: "Best price, guaranteed", sub: "Book direct and save" },
    { id: "longstay", icon: CalendarRange, title: "Long stay savings",      sub: "Better rates from 7 nights" },
    { id: "checkin",  icon: Clock,         title: "Self check-in",          sub: "Arrive anytime, 24/7" },
    { id: "wifi",     icon: Wifi,          title: "Superfast Wi-Fi",        sub: "Work, stream, live" },
  ],
  slowdown: {
    overline: "Designed for how you live",
    line_a: "Slow down.",
    line_b_accent: "Settle in.",
    body: "Whether you're here for a week, a month, or the season — this is your space to get things done, and feel at home.",
    cta: { label: "Explore the studio", href_resolver: (id) => (id ? `/properties/${id}` : "/properties") },
    cards: [
      { id: "rest", title: "Rest well",         sub: "A proper bed, quality linens, and a peaceful street." },
      { id: "need", title: "Everything you need", sub: "Full kitchen, workspace, and all the essentials." },
      { id: "focus", title: "Space to focus",    sub: "A proper desk, fast Wi-Fi, and natural light." },
    ],
  },
  longstay: {
    overline: "For the long stay",
    line_a: "For the people who don't come on",
    accent: "holiday.",
    body: "Most short-lets are designed for two nights. Ours is designed for two months. No marketplace fees, no rolling tenancy, no surprises. Bills, cleaning, fast Wi-Fi and a proper desk are already included — so you can land in London and start working.",
    tiers: [
      { nights: 7,  label: "A week",      tagline: "For the project trip or in-office sprint." },
      { nights: 14, label: "A fortnight", tagline: "Bridge the gap between flights and flats." },
      { nights: 21, label: "Three weeks", tagline: "Settle a transition without a tenancy." },
      { nights: 28, label: "A month",     tagline: "Relocate without the rental deposit." },
      { nights: 60, label: "Two months",  tagline: "A real London base, fully furnished." },
      { nights: 90, label: "A season",    tagline: "Call it home, on flexible terms." },
    ],
    includes: [
      { icon: Wifi,      label: "Fibre Wi-Fi",         note: "300 Mb · video-call ready" },
      { icon: Briefcase, label: "Proper desk",         note: "Ergonomic chair · daylight window" },
      { icon: Coffee,    label: "Full kitchen",        note: "Espresso bar · induction hob" },
      { icon: BedDouble, label: "Hotel-grade linens",  note: "Refreshed weekly" },
      { icon: KeyRound,  label: "Self check-in",       note: "Lockbox · arrive any hour" },
      { icon: Sparkles,  label: "Bills included",      note: "Utilities · cleaning · linens" },
    ],
    audience_label: "Built for",
    audiences: [
      "Business travellers", "Professionals relocating", "NHS staff", "Remote workers",
      "Digital nomads", "Contractors", "Students", "House-move bridges",
    ],
    footer_note: "Long-stay rates are tailored to the dates. Real reductions on 14, 28 and 60+ night bookings — quoted in writing, without negotiation.",
    cta: { label: "Request a long-stay quote" },
  },
  neighbourhood: {
    overline: "The neighbourhood",
    line_a: "A",
    accent: "postcode",
    line_b: "with character.",
    body: "W6 sits between the river and the West End. Old pubs, indie roasters, riverside walks, and tube connections that get you to any meeting on time.",
    cta_label: "Open the local guide",
    cards: [
      { kind: "Coffee",  name: "The Hammersmith Coffee Co.",        note: "Tucked off King Street. Single-origin, owner-roasted." },
      { kind: "Eat",     name: "River Café",                         note: "Eight minutes by foot. A London institution since 1987." },
      { kind: "Walk",    name: "Thames Path · Hammersmith Bridge",   note: "Start at the bridge, finish at Barnes. A two-mile reset." },
      { kind: "Transit", name: "Hammersmith Station",                note: "Three lines · 22 min to Mayfair." },
    ],
  },
  host_note: {
    overline: "A note from your host",
    greeting: "A note from your host —",
    body: [
      "London asks a lot of the people who come here. It is loud, it is fast, and the rooms are usually too small.",
      "Our studio in Hammersmith is the opposite. Quiet street, large windows, a proper bed, a proper desk. The kettle is good. The Wi-Fi is faster than your office.",
      "Whether you have come for a meeting or a season — make yourself at home.",
    ],
    signature: "— West London Stays",
  },
  epilogue: {
    overline: "Reserve",
    line_a_accent: "Reserve",
    line_a_rest: " your week.",
    line_b: "Or your season.",
    body: "Real availability. Real reductions for longer stays. No marketplace fees — book direct and a real host will message you back, usually within the hour.",
    cta_primary: "Reserve the studio",
    cta_whatsapp: "Message the host",
  },
};

function nightsFromNow(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function Home() {
  const [studio, setStudio] = useState(null);
  useEffect(() => {
    fetchProperties().then((list) => {
      const published = list?.find((p) => p.bookable) || list?.[0];
      setStudio(published || null);
    });
  }, []);

  // CMS-managed copy (merged with the baked-in defaults above).
  // The `promises` and `longstay.includes` arrays carry icon components which
  // can't be JSON-serialised — we re-attach the icon refs after the CMS merge
  // so the admin can edit titles + subs without losing the icon mapping.
  const ICONS = { BadgeCheck, CalendarRange, Clock, Wifi, Briefcase, Coffee, BedDouble, KeyRound, Sparkles };
  const rawCMS = useCMS("home", HOMEPAGE_CONTENT);
  const C = {
    ...rawCMS,
    promises: (rawCMS.promises || HOMEPAGE_CONTENT.promises).map((p, i) => ({
      ...p,
      icon: typeof p.icon === "string" ? (ICONS[p.icon] || HOMEPAGE_CONTENT.promises[i]?.icon) : (p.icon || HOMEPAGE_CONTENT.promises[i]?.icon),
    })),
    longstay: {
      ...rawCMS.longstay,
      includes: (rawCMS.longstay?.includes || HOMEPAGE_CONTENT.longstay.includes).map((inc, i) => ({
        ...inc,
        icon: typeof inc.icon === "string" ? (ICONS[inc.icon] || HOMEPAGE_CONTENT.longstay.includes[i]?.icon) : (inc.icon || HOMEPAGE_CONTENT.longstay.includes[i]?.icon),
      })),
    },
    hero: {
      ...rawCMS.hero,
      cta_primary: {
        ...rawCMS.hero?.cta_primary,
        href_resolver: HOMEPAGE_CONTENT.hero.cta_primary.href_resolver,
      },
    },
    slowdown: {
      ...rawCMS.slowdown,
      cta: {
        ...rawCMS.slowdown?.cta,
        href_resolver: HOMEPAGE_CONTENT.slowdown.cta.href_resolver,
      },
    },
  };
  const images = studio?.images || [];
  const heroImg = C.hero?.image || images[0];
  const cardImgs = C.slowdown?.card_images || [];
  const studioImgs = [0, 1, 2].map((i) => cardImgs[i] || images[i] || heroImg);

  return (
    <div data-testid="home-editorial" className="bg-[var(--ed-bg)]">
      {/* ─────────────────────────────────────────────────────────────
          1. HERO — 100vh, edge-to-edge, soft left gradient only
      ───────────────────────────────────────────────────────────────── */}
      <section
        data-testid="home-hero"
        className="relative w-full overflow-hidden"
        style={{ height: "100vh", minHeight: "640px" }}
      >
        <div className="absolute inset-0">
          {heroImg && (
            <SmartImage
              src={heroImg}
              alt="Chic Hammersmith Studio — the bedroom"
              containerClassName="w-full h-full"
              className="w-full h-full object-cover"
              loading="eager"
            />
          )}
          {/* Video overlay — replaces the hero image when it can autoplay.
              Falls back gracefully to the SmartImage below if the browser
              blocks autoplay or can't decode either source (onError hides). */}
          <video
            data-testid="home-hero-video"
            poster={heroImg || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            tabIndex={-1}
            className="absolute inset-0 block w-full h-full object-cover pointer-events-none"
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", margin: 0, padding: 0, border: 0 }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          >
            <source src="/media/hero.webm" type="video/webm" />
            <source src="/media/hero.mp4" type="video/mp4" />
          </video>
        </div>

        {/* Soft top gradient — keeps the navigation legible over any photograph.
            Stays calm (~22% peak alpha) and fades by 220px so the image stays bright. */}
        <div
          className="absolute top-0 left-0 right-0 h-[220px] pointer-events-none"
          style={{
            background: "linear-gradient(180deg, rgba(20,17,12,0.55) 0%, rgba(20,17,12,0.22) 45%, rgba(20,17,12,0) 100%)",
          }}
        />

        {/* Soft left-side gradient — only as much darkness as readability requires.
            Stays transparent across the centre and right so the photograph remains bright. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(20,17,12,0.62) 0%, rgba(20,17,12,0.38) 18%, rgba(20,17,12,0.10) 42%, rgba(20,17,12,0) 62%)",
          }}
        />

        {/* Hero copy — bottom-left, editorial composition */}
        <div className="absolute bottom-0 left-0 w-full sm:w-[80%] lg:w-[64%] xl:w-[58%] ed-container pb-14 sm:pb-16 lg:pb-24">
          <p
            data-testid="hero-overline"
            className="font-sans text-[0.7rem] sm:text-[0.74rem] tracking-[0.36em] uppercase mb-6 lg:mb-8"
            style={{ color: "#9AC4A9" }}
          >
            {C.hero.overline}
          </p>
          <h1
            data-testid="hero-headline"
            className="font-serif text-white leading-[1.04] mb-7 lg:mb-9"
            style={{
              fontWeight: 400,
              fontSize: "clamp(2.4rem, 6vw, 5.5rem)",
              textShadow: "0 4px 32px rgba(0,0,0,0.45)",
            }}
          >
            {C.hero.line_a}
            <br />
            {C.hero.line_b}
            <br />
            <em
              style={{
                color: "#7FB39A",
                fontStyle: "italic",
                fontWeight: 400,
              }}
            >
              {C.hero.line_b_accent}
            </em>
          </h1>
          <p
            data-testid="hero-subhead"
            className="text-white/95 font-sans text-base sm:text-lg max-w-xl mb-9 lg:mb-11 leading-relaxed"
            style={{ textShadow: "0 2px 18px rgba(0,0,0,0.4)" }}
          >
            {C.hero.sub}
          </p>
          <div className="flex flex-col sm:flex-row gap-3.5">
            <Link
              to={C.hero.cta_primary.href_resolver(studio?.id)}
              data-testid="hero-cta-book"
              className="ed-btn-primary-light"
            >
              {C.hero.cta_primary.label}
            </Link>
            <a
              href={C.hero.cta_secondary.href}
              data-testid="hero-cta-read"
              className="ed-btn-ghost-light"
            >
              {C.hero.cta_secondary.label}
            </a>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          2. PROMISES STRIP — 4 trust signals on warm ivory
      ───────────────────────────────────────────────────────────────── */}
      <section
        data-testid="home-promises"
        className="py-9 lg:py-11 border-y border-[var(--ed-rule)]"
        style={{ background: "var(--ed-bg)" }}
      >
        <div className="ed-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-7 gap-x-6 lg:gap-x-10">
            {C.promises.map(({ id, icon: Icon, title, sub }, i) => (
              <div
                key={id}
                data-testid={`promise-${id}`}
                className={`flex items-center gap-4 ${i > 0 ? "lg:border-l lg:border-[var(--ed-rule)] lg:pl-6" : ""}`}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "var(--ed-emerald-tint)" }}
                >
                  <Icon size={20} strokeWidth={1.6} color="var(--ed-emerald)" />
                </div>
                <div className="leading-tight">
                  <p className="font-serif text-[1rem] lg:text-[1.05rem] text-[var(--ed-ink)] mb-1">{title}</p>
                  <p className="text-[0.82rem] text-[var(--ed-ink-soft)]">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          3. SLOW DOWN. SETTLE IN. — designed-for-how-you-live
      ───────────────────────────────────────────────────────────────── */}
      <section id="the-studio" className="py-20 lg:py-28" data-testid="home-slowdown">
        <div className="ed-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <p className="font-sans text-[0.7rem] tracking-[0.36em] uppercase mb-5" style={{ color: "var(--ed-emerald)" }}>
                {C.slowdown.overline}
              </p>
              <h2 className="font-serif text-[var(--ed-ink)] mb-6" style={{ fontSize: "clamp(2.2rem, 4vw, 3.4rem)", lineHeight: 1.05, fontWeight: 500 }}>
                {C.slowdown.line_a}
                <br />
                <em style={{ color: "var(--ed-emerald)", fontStyle: "italic", fontWeight: 400 }}>
                  {C.slowdown.line_b_accent}
                </em>
              </h2>
              <p className="ed-body mb-9 max-w-md">{C.slowdown.body}</p>
              <Link to={C.slowdown.cta.href_resolver(studio?.id)} data-testid="slowdown-cta" className="ed-btn-ghost">
                {C.slowdown.cta.label}
              </Link>
            </div>

            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-6">
                {C.slowdown.cards.map((card, i) => (
                  <article key={card.id} data-testid={`slowdown-card-${card.id}`}>
                    <div className="aspect-[3/4] ed-photo mb-4">
                      <SmartImage
                        src={studioImgs[i] || heroImg}
                        alt={card.title}
                        containerClassName="w-full h-full"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="font-serif text-[1.2rem] text-[var(--ed-ink)] mb-2">{card.title}</h3>
                    <p className="text-[0.92rem] text-[var(--ed-ink-soft)] leading-relaxed">{card.sub}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="ed-rule" />

      {/* ─────────────────────────────────────────────────────────────
          4. THE LONG STAY — audiences + tiers + inclusions
      ───────────────────────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28" style={{ background: "var(--ed-paper)" }} data-testid="home-longstay">
        <div className="ed-container">
          <div className="max-w-3xl mb-12">
            <p className="font-sans text-[0.7rem] tracking-[0.36em] uppercase mb-4" style={{ color: "var(--ed-emerald)" }}>
              {C.longstay.overline}
            </p>
            <h2 className="font-serif text-[var(--ed-ink)] mb-6" style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.6rem)", lineHeight: 1.05, fontWeight: 500 }}>
              {C.longstay.line_a}{" "}
              <em style={{ color: "var(--ed-emerald)", fontStyle: "italic", fontWeight: 400 }}>{C.longstay.accent}</em>
            </h2>
            <p className="ed-body">{C.longstay.body}</p>
          </div>

          {/* Audience strip */}
          <div className="mb-12 pb-9 border-b border-[var(--ed-rule)] flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="font-sans text-[0.66rem] tracking-[0.32em] uppercase" style={{ color: "var(--ed-emerald)" }}>
              {C.longstay.audience_label}
            </span>
            {C.longstay.audiences.map((a, i) => (
              <span key={a} className="flex items-center gap-6">
                <span className="font-serif italic text-[1rem] text-[var(--ed-ink)]">{a}</span>
                {i < C.longstay.audiences.length - 1 && <span className="text-[var(--ed-emerald)]/45">·</span>}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-9 mb-14" data-testid="longstay-tiers">
            {C.longstay.tiers.map((t) => (
              <div key={t.nights} className="border-l-2 border-[var(--ed-emerald)] pl-5">
                <p className="ed-issue mb-2" style={{ fontSize: "0.85rem" }}>{String(t.nights).padStart(2, "0")} nights</p>
                <p className="font-serif text-3xl text-[var(--ed-ink)] mb-2" style={{ fontWeight: 500 }}>{t.label}.</p>
                <p className="text-[0.95rem] text-[var(--ed-ink-soft)] italic">{t.tagline}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-8">
            {C.longstay.includes.map(({ icon: Icon, label, note }) => (
              <div key={label} className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full" style={{ background: "var(--ed-emerald-tint)" }}>
                  <Icon size={18} strokeWidth={1.6} color="var(--ed-emerald)" />
                </div>
                <p className="font-serif text-[var(--ed-ink)] text-base leading-tight mb-1">{label}</p>
                <p className="text-[0.75rem] text-[var(--ed-ink-muted)] leading-snug">{note}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-9 border-t border-[var(--ed-rule)]">
            <p className="ed-body max-w-md">{C.longstay.footer_note}</p>
            <Link
              to={`/properties${studio ? `/${studio.id}?check_in=${nightsFromNow(7)}&check_out=${nightsFromNow(21)}` : ""}`}
              data-testid="longstay-cta"
              className="ed-btn-primary self-start sm:self-auto"
            >
              {C.longstay.cta.label}
            </Link>
          </div>
        </div>
      </section>

      <div className="ed-rule" />

      {/* ─────────────────────────────────────────────────────────────
          5. NEIGHBOURHOOD
      ───────────────────────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28" data-testid="home-neighbourhood">
        <div className="ed-container">
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between mb-12 gap-4">
            <div className="max-w-xl">
              <p className="font-sans text-[0.7rem] tracking-[0.36em] uppercase mb-4" style={{ color: "var(--ed-emerald)" }}>
                {C.neighbourhood.overline}
              </p>
              <h2 className="font-serif text-[var(--ed-ink)] mb-5" style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.6rem)", lineHeight: 1.05, fontWeight: 500 }}>
                {C.neighbourhood.line_a}{" "}
                <em style={{ color: "var(--ed-emerald)", fontStyle: "italic", fontWeight: 400 }}>{C.neighbourhood.accent}</em>{" "}
                {C.neighbourhood.line_b}
              </h2>
              <p className="ed-body">{C.neighbourhood.body}</p>
            </div>
            <Link to="/why-choose-us" className="ed-link self-start sm:self-auto" data-testid="neighbourhood-cta">
              {C.neighbourhood.cta_label}
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7" data-testid="neighbourhood-cards">
            {C.neighbourhood.cards.map((item, i) => (
              <article
                key={item.name}
                className="bg-[var(--ed-bg)] border border-[var(--ed-rule)] p-2 transition-transform duration-500 hover:-translate-y-1"
                style={{ rotate: i % 2 === 0 ? "-0.3deg" : "0.4deg" }}
              >
                <div className="aspect-[4/5] ed-photo bg-[var(--ed-paper)] flex items-center justify-center overflow-hidden">
                  {item.image ? (
                    <SmartImage src={item.image} alt={item.name} containerClassName="w-full h-full" className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-serif text-[2.2rem] text-[var(--ed-ink)]/15 select-none">{String(i + 1).padStart(2, "0")}</span>
                  )}
                </div>
                <div className="px-2 pt-4 pb-3">
                  <p className="font-sans text-[0.6rem] tracking-[0.32em] uppercase mb-2" style={{ color: "var(--ed-emerald)" }}>{item.kind}</p>
                  <p className="font-serif text-xl text-[var(--ed-ink)] leading-tight mb-2">{item.name}</p>
                  <p className="text-[0.85rem] text-[var(--ed-ink-soft)] leading-relaxed">{item.note}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="ed-rule" />

      {/* ─────────────────────────────────────────────────────────────
          6. HOST'S NOTE
      ───────────────────────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28" style={{ background: "var(--ed-paper)" }} data-testid="home-host-note">
        <div className="ed-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] ed-photo">
                <SmartImage src={studioImgs[1] || heroImg} alt="The host's space" containerClassName="w-full h-full" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="font-sans text-[0.7rem] tracking-[0.36em] uppercase mb-4" style={{ color: "var(--ed-emerald)" }}>
                {C.host_note.overline}
              </p>
              <p className="ed-issue mb-7" style={{ fontSize: "1rem" }}>{C.host_note.greeting}</p>
              {C.host_note.body.map((p, i) => (
                <p key={i} className="font-serif text-2xl sm:text-3xl mb-5 leading-snug" style={{ color: "var(--ed-ink)", fontWeight: 400 }}>{p}</p>
              ))}
              <div className="flex items-center gap-4 mt-8">
                <span className="ed-rule-short" />
                <p className="ed-issue" style={{ fontSize: "0.95rem" }}>{C.host_note.signature}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          6.5 GUEST REVIEWS — verified Airbnb testimonials
      ───────────────────────────────────────────────────────────────── */}
      <GuestReviews />

      {/* ─────────────────────────────────────────────────────────────
          7. EPILOGUE — dark closing block
      ───────────────────────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-[var(--ed-ink)] text-white" data-testid="home-epilogue">
        <div className="ed-container">
          <div className="max-w-3xl">
            <p className="font-sans text-[0.7rem] tracking-[0.36em] uppercase mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>
              {C.epilogue.overline}
            </p>
            <h2 className="font-serif mb-7 text-white" style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.6rem)", lineHeight: 1.05, fontWeight: 500 }}>
              <em style={{ color: "#7FB39A", fontStyle: "italic", fontWeight: 400 }}>{C.epilogue.line_a_accent}</em>
              {C.epilogue.line_a_rest}
              <br />
              {C.epilogue.line_b}
            </h2>
            <p className="ed-body mb-9" style={{ color: "rgba(255,255,255,0.75)" }}>{C.epilogue.body}</p>
            <div className="flex flex-col sm:flex-row gap-3.5">
              <Link to={studio ? `/properties/${studio.id}` : "/properties"} data-testid="epilogue-cta-book" className="ed-btn-primary-light">
                {C.epilogue.cta_primary} <ArrowRight size={14} strokeWidth={1.8} />
              </Link>
              <a
                href="https://wa.me/447474759947"
                target="_blank"
                rel="noreferrer"
                data-testid="epilogue-cta-whatsapp"
                className="ed-btn-ghost-light"
              >
                <MessageCircle size={14} /> {C.epilogue.cta_whatsapp}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
